import React, { useEffect, useRef, useState } from 'react';
import './provider.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  copyTextToClipboard,
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getProviderData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import ProviderDTO from '../../../dto/ProviderDTO';
import ProviderForm from './ProviderForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import { providerAdditionalColumns, providerColumns } from './providerColumns';
import {
  deleteProvider, loadProviderById, loadProviderByIdClear, loadProviderList,
} from '../actions';
import { AUTHORIZED_MODULE, PROVIDER_MODULE } from '../../../configs/moduleNames';
import { ROLE_TYPE } from '../../../configs';
import { getUserRole } from '../../../utils/routeByRoles';
import TextInput from '../../../components/fields/TextInput/TextInput';

export default function ProviderPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showReadOnly, setShowReadOnly] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    providerListMeta,
    loadingProviderList,
    providerList,
    loadingCurrentProvider,
    currentProvider,
    loadingCreateProvider,
    loadingEditProviderById,
    loadingDeleteProvider,
  } = useSelector(getProviderData);
  const { formatMessage } = useIntl();
  const rawAuthorizedData = useSelector(state => state[AUTHORIZED_MODULE]?.authorizedData || {});
  const isAdmin = getUserRole(rawAuthorizedData) === ROLE_TYPE.ADMIN;

  const loadProvider = (id, canEdit = false) => {
    if (id) {
      dispatch(loadProviderById(id))
        .then(() => {
          setShowReadOnly(!canEdit);
          setShowForm(true);
        })
        .catch(response => errorNotification('Загрузка провайдеров', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[PROVIDER_MODULE].providerListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    const p1 = dispatch(loadProviderList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue,
  } = useFilterForm({}, loadData, ['provider_id']);
  const vid = getValues('provider_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadProvider(vid, isAdmin), [vid, isAdmin]);
  const DTO = new ProviderDTO();
  const attributes = DTO.getAttributesByName();

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.id === item.id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    setShowReadOnly(false);
    setShowForm(true);
    clearCurrentState();
  };

  const handleEdit = item => event => {
    if (!item || !item.id || !isAdmin) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    loadProvider(item.id, true);
  };

  const handleView = item => event => {
    if (!item?.id) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    loadProvider(item.id, false);
  };

  const handleDelete = item => event => {
    if (!item.id) {
      errorNotification('Ошибка', 'Нет ID');
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление провайдера # ${item.id}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteProvider(item.id)).then(() => {
        if (selectedRow?.id === item.id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление провайдера', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление провайдера', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    copyTextToClipboard(`${baseUrl}?provider_id=${item.id}`).then(success => {
      if (success) {
        successNotification('Копирование ссылки', 'Ссылка скопирована');
      } else {
        errorNotification('Копирование ссылки', 'Не удалось скопировать ссылку');
      }
    });
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('provider_id', '');
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadProviderByIdClear()).catch(responseLogWithNotification);
  };

  const handleDoubleClick = (item, event) => {
    if (!item || !item.id) {
      return;
    }
    if (isAdmin) {
      handleEdit(item)(event);
    } else {
      handleView(item)(event);
    }
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}>
            Копировать ссылку
          </Button>
          {isAdmin ? (
            <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
              Редактировать
            </Button>
          ) : null}
          {isAdmin ? (
            <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
              Удалить
            </Button>
          ) : null}
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.id === selectedRow.id) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingProviderList || loadingCurrentProvider || loadingDeleteProvider || loadingCreateProvider || loadingEditProviderById} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.provider' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.provider_name}
            hideSmallButtons
            showArrowIcon
            usePortal
            placeholder="Наименование"
            className="no-border"
          />
          <div className="filter-sep" />
          {isAdmin ? (
            <Button variant="second" onClick={handleCreate}>
              <Icons
                customIcon
                width={24}
                height={24}
                icon="plus"
              />
            </Button>
          ) : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={handleDoubleClick}
                rowClassFormatter={rowClassFormatter}
                data={providerList}
                columns={providerColumns}
                additionalColumns={providerAdditionalColumns}
                name="provider-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={providerListMeta} list={providerList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showForm ? (
        <ProviderForm
          currentProvider={currentProvider}
          isReadOnly={showReadOnly}
          loadData={loadData}
          onClose={handleClose}
        />
      ) : null}
    </div>
  );
}
